import React, { useRef } from "react";
import { Box, Chip, Typography } from "@mui/material";
import FallbackLoader from "../loading/FallBackLoader";

interface Room {
  id: string;
  displayName: string;
}

interface ActivityTabRoomsProps {
  roomData: Room[] | undefined;
  isRoomLoading: boolean;
  onItemDrop: (
    itemId: string,
    itemName: string,
    itemType: string,
    x: number,
    y: number
  ) => void;
}

interface DraggedItem {
  id: string;
  name: string;
  type: string;
}

const ActivityTabRooms: React.FC<ActivityTabRoomsProps> = ({
  roomData,
  isRoomLoading,
  onItemDrop,
}) => {
  const isTouchDevice =
    typeof window !== "undefined" && "ontouchstart" in window;
  // For mobile dragging
  const draggedItemRef = useRef<DraggedItem | null>(null);
  const draggingRef = useRef<boolean>(false); // Are we actively dragging?
  const isLongPressRef = useRef<boolean>(false); // Did we confirm a long press?
  const touchTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Tracks the 200ms timer

  if (isRoomLoading) {
    return <FallbackLoader />;
  }

  const handleTouchStart = (e: React.TouchEvent<HTMLElement>, room: Room) => {
    draggingRef.current = false;
    isLongPressRef.current = false;
    console.log(e);

    touchTimeoutRef.current = setTimeout(() => {
      isLongPressRef.current = true;
    }, 200);

    draggedItemRef.current = {
      id: room.id,
      name: room.displayName,
      type: "Room",
    };
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLElement>) => {
    if (isLongPressRef.current) {
      return;
    }
    if (!draggingRef.current) {
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
        touchTimeoutRef.current = null;
      }

      draggingRef.current = true;

      const touch = e.touches[0];
      const chipElement = document.createElement("div");
      chipElement.id = "dragging-chip";
      chipElement.style.position = "fixed";
      chipElement.style.left = `${touch.clientX - 50}px`;
      chipElement.style.top = `${touch.clientY - 25}px`;
      chipElement.style.zIndex = "9999";
      chipElement.style.opacity = "0.8";
      chipElement.style.padding = "4px 8px";
      chipElement.style.backgroundColor = "#444";
      chipElement.style.color = "white";
      chipElement.textContent = draggedItemRef.current?.name ?? "Room";
      document.body.appendChild(chipElement);
    }

    if (draggingRef.current) {
      e.preventDefault();
      const touch = e.touches[0];
      const chip = document.getElementById("dragging-chip");
      if (chip) {
        chip.style.left = `${touch.clientX - 50}px`;
        chip.style.top = `${touch.clientY - 25}px`;
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLElement>) => {
    console.log("here???");
    if (touchTimeoutRef.current) {
      clearTimeout(touchTimeoutRef.current);
      touchTimeoutRef.current = null;
    }
    const chip = document.getElementById("dragging-chip");
    if (chip) {
      document.body.removeChild(chip);
    }

    if (draggingRef.current && draggedItemRef.current) {
      const touch = e.changedTouches[0];
      console.log("dsffds");
      onItemDrop(
        draggedItemRef.current.id,
        draggedItemRef.current.name,
        draggedItemRef.current.type,
        touch.clientX,
        touch.clientY
      );
    }

    draggingRef.current = false;
    isLongPressRef.current = false;
    draggedItemRef.current = null;
  };

  const handleTouchCancel = () => {
    if (touchTimeoutRef.current) {
      clearTimeout(touchTimeoutRef.current);
      touchTimeoutRef.current = null;
    }
    const chip = document.getElementById("dragging-chip");
    if (chip) {
      document.body.removeChild(chip);
    }

    draggingRef.current = false;
    isLongPressRef.current = false;
    draggedItemRef.current = null;
  };

  const handleDragStart = (e: React.DragEvent<HTMLElement>, room: Room) => {
    e.dataTransfer.setData("item_id", room.id);
    e.dataTransfer.setData("item_name", room.displayName);
    e.dataTransfer.setData("item_type", "Room");

    const chipElement = e.currentTarget.cloneNode(true) as HTMLElement;
    chipElement.style.position = "absolute";
    chipElement.style.width = "100px";
    chipElement.style.height = "100px";
    chipElement.style.display = "flex";
    chipElement.style.alignItems = "center";
    chipElement.style.justifyContent = "center";
    chipElement.style.backgroundColor = "#444";
    chipElement.style.color = "white";
    chipElement.style.cursor = "grab";
    document.body.appendChild(chipElement);

    e.dataTransfer.setDragImage(chipElement, 50, 50);
  };

  return (
    <Box>
      <Typography
        variant="subtitle1"
        sx={{ marginBottom: "12px", fontWeight: "bold" }}
      >
        Rooms
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {roomData?.map((room) => (
          <Chip
            key={room.id}
            label={room.displayName}
            draggable={isTouchDevice ? false : true}
            onDragStart={
              isTouchDevice ? undefined : (e) => handleDragStart(e, room)
            }
            onTouchStart={(e) => handleTouchStart(e, room)}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
            sx={{
              backgroundColor: "#444",
              color: "white",
              "&:hover": { backgroundColor: "#555" },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default ActivityTabRooms;
